import { useSaveChatResource } from "~/ai-chat/feature/use-save-chat-resource";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { ChatSaveTranscriptDialog } from "~/ai-chat/ui/chat-save-transcript-dialog";
import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";
import { useAllowedInfosecLevels, useInfosecFeatureEnabled } from "~/hooks";

interface ChatSaveTranscriptProps {
  messages: ChatTranscriptItem[];
  defaultName: string;
  onClose: () => void;
  // Opens the resource share dialog for the just-saved chat. Owned by the widget
  // so it outlives this dialog (which unmounts on save success).
  onShare?: (resourceId: string, displayName: string) => void;
}

// The save-to-library flow, mounted only while its dialog is open so resolving
// the user's allowed infosec levels (which fetches the org user list) never runs
// on every chat-widget mount.
export function ChatSaveTranscript({ messages, defaultName, onClose, onShare }: ChatSaveTranscriptProps) {
  const { save, isSaving } = useSaveChatResource(onShare);
  const featureEnabled = useInfosecFeatureEnabled();
  const platformInfosec = useGetPlatformInfosecSettings();
  const infosecLevels = useAllowedInfosecLevels(undefined, { enabled: featureEnabled });

  // Off feature shell ⇒ definitively false (no classification concept), so Save
  // isn't blocked forever. On an infosec deployment, `undefined` while the setting
  // resolves keeps Save blocked so an unclassified save can't slip through.
  const infosecEnabled = featureEnabled ? platformInfosec.data?.enabled : false;

  return (
    <ChatSaveTranscriptDialog
      open
      defaultName={defaultName}
      infosecEnabled={infosecEnabled}
      infosecLevels={infosecLevels}
      isSaving={isSaving}
      onSave={(name, infosecLevelId) => {
        void save(name, messages, infosecLevelId).then((ok) => {
          if (ok) onClose();
        });
      }}
      onCancel={onClose}
    />
  );
}
